import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
  DynamicFormInputItems,
  IAppError,
  IForm,
  IFormInput,
  IFormInputError,
  IFormInputs,
  IValidationData,
} from "config/types";
import IFormValidations from "modules/hooks/form/Validations";
import { useTranslation } from "modules/hooks/useTranslation";
import { eventFired } from "store/actions/analytics";
import { setFormFlowDirty } from "store/actions/flow";
import { processValidationErrors } from "utils/error";
import { buildFormInputs, IResetValues, resetFormInputs } from "utils/form";

import { Dispatch } from "@reduxjs/toolkit";
import {
  flatten,
  forOwn,
  get,
  isArray,
  isEmpty,
  isEqual,
  isNumber,
  isString,
  some,
} from "lodash-es";
import { DateTime } from "luxon";

export interface IOptions {
  showInputErrorMessages: boolean;
  allValidInputsRequired?: boolean;
  onSubmit?(form: IForm): Promise<void> | void;
}

interface IReturnValue {
  form: IForm;
  inputs: IFormInputs;
}

const VALIDATION_ANALYTICS_EVENTS = {
  "errors.invalid_date_range": ({
    dispatch,
    key,
    value,
  }: {
    dispatch: Dispatch;
    key: string;
    value: IFormInput["value"];
  }) => {
    if (key === "prequalification-dob") {
      const birthdate = DateTime.fromISO(value as string);

      dispatch(
        eventFired({
          event: "prequal_birthday_validationerror",
          experienceLocation: "ONBOARDING",
          attribute: {
            birthdate: birthdate.isValid ? birthdate.toFormat("yyyy-MM-dd") : "",
          },
        })
      );
    }
  },
};

export const useForm = (
  fieldData: Partial<IFormInput>[],
  { onSubmit: onSubmitCb, showInputErrorMessages }: IOptions
): IReturnValue => {
  const dispatch = useDispatch();
  const formInputs = buildFormInputs(fieldData);
  const [inputs, setInputs] = useState(formInputs);
  const [loading, setLoading] = useState(false);
  const [allRequiredFieldsFilled, setAllRequiredFieldsFilled] = useState(() => {
    let allFilled = true;
    forOwn(formInputs, input => {
      let value = input.value;
      if (
        input.id === "phoneNumber" &&
        input.value != null &&
        Object.prototype.hasOwnProperty.call(input.value, "phoneNumber")
      ) {
        value = (input.value as Record<string, string>).phoneNumber;
      }
      if (input.inputType === "inputCheckbox") {
        value = input.value === true ? "true" : "";
      }
      if (input.required && !isNumber(value) && isEmpty(value)) {
        allFilled = false;
      }
    });
    return allFilled;
  });
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [formvalid, setFormValid] = useState(() => {
    let valid = true;
    forOwn(formInputs, input => {
      const errors = flatten([input.error]);
      if (errors.length) {
        valid = false;
      }
    });
    return valid;
  });
  const [isFormDirtied, setIsFormDirtied] = useState(false);
  const [isFormInitialized, setIsFormInitialized] = useState(true);
  const { t } = useTranslation();

  /*
  ..................
  ~~~~~ START VALIDATIONS ~~~~~
  ..................
  */
  const setRequiredFieldsFilled = (formInputs = inputs) => {
    let allFilled = true;

    forOwn(formInputs, input => {
      let value = input.value;

      // HACK: For Beneficiary Phone Numbers, we have a half international/half
      // US-only set up where the countryCode and phoneNumber are stored
      // separately in the `input.value`. This raises the `phoneNumber` up to
      // the top level. If we pivot back to allowing more countries, this will
      // need to be refactored.
      if (
        input.id === "phoneNumber" &&
        input.value != null &&
        Object.prototype.hasOwnProperty.call(input.value, "phoneNumber")
      ) {
        value = (input.value as Record<string, string>).phoneNumber;
      }

      if (input.inputType === "inputCheckbox") {
        value = input.value === true ? "true" : "";
      }

      if (input.required && !isNumber(value) && isEmpty(value)) {
        allFilled = false;
      }
    });

    setAllRequiredFieldsFilled(allFilled);
  };

  const validateField = (id: IFormInput["id"]) => {
    const errors: IFormInputError[] = [];

    const requiredValidation = IFormValidations["required"];

    const input = get(inputs, id);
    const value = get(input, ["value"], "");

    if (!input) {
      return;
    }

    if (input.required && !requiredValidation.validation(value)) {
      errors.push({
        code: requiredValidation.errorCode,
        message: t("errors.required", "Required"),
      });
    }

    if (!!value) {
      input.validations.forEach((validation: IFormInput["validations"][number]) => {
        const isObjectValidation = !isString(validation) && "name" in validation;
        const validationName = isString(validation)
          ? validation
          : (validation as { name: string }).name;
        const formValidation = IFormValidations[validationName];

        const validationData: IValidationData = {
          inputs,
          validation: isObjectValidation ? (validation as { [key: string]: unknown }) : {},
          inputId: input.id,
        };

        if (!formValidation.validation(value, validationData)) {
          const analyticsEvent = get(VALIDATION_ANALYTICS_EVENTS, formValidation.errorCode);

          if (analyticsEvent) {
            analyticsEvent({
              dispatch,
              key: input.key,
              value: input.value,
            });
          }

          errors.push({
            code: formValidation.errorCode,
            message: t(formValidation.errorCode, formValidation.defaultError),
          });
        }
      });
    }

    updateInput(id, { touched: true, error: errors });
  };

  const validateAllFields = () => {
    forOwn(inputs, input => {
      validateField(input.id);
    });

    const isFormValid = setFormValidity();

    return isFormValid;
  };

  const setFormValidity = (formInputs = inputs) => {
    let valid = true;

    forOwn(formInputs, input => {
      const errors = flatten([input.error]);
      if (errors.length) {
        valid = false;
      }
    });

    setFormValid(valid);

    return valid;
  };

  const setFormDirtied = () => {
    const areInputValuesDirtied = some(inputs, input => !isEqual(input.value, input.pristineValue));

    setIsFormDirtied(areInputValuesDirtied);
    dispatch(setFormFlowDirty(areInputValuesDirtied));

    return areInputValuesDirtied;
  };

  /*
  ..................
  ~~~~~ START INITIIALIZE STATE ~~~~~
  ..................
  */
  const initializeFormState = (formInputs: IFormInputs | Partial<IFormInput>[]) => {
    const newInputs = isArray(formInputs) ? buildFormInputs(fieldData) : formInputs;
    setInputs(newInputs);
    setFormValidity(newInputs);
    setRequiredFieldsFilled(newInputs);
    setIsFormInitialized(true);
  };

  /*
  ..................
  ~~~~~ END INITIIALIZE STATE ~~~~~
  ..................
  */

  /*
  ..................
  ~~~~~ END VALIDATIONS ~~~~~
  ..................
  */

  /*
  ..................
  ~~~~~ START HELPERS ~~~~~
  ..................
  */
  const updateInput = (id: IFormInput["id"], data: { [key: string]: unknown }) => {
    // Mutate inputs[id] in place so that multiple updateInput calls within the
    // same event (e.g. Smarty address filling several fields in a loop) each see
    // the previous call's changes via the shared `inputs` closure reference.
    const newInput: IFormInput = Object.assign(inputs[id], data) as IFormInput;
    const newInputs = { ...inputs, [id]: newInput };
    setInputs(newInputs);
    return newInputs;
  };

  const getFormInput = (id: IFormInput["id"]) => {
    return inputs[id] || {};
  };

  const getInputError = (id: IFormInput["id"]) => {
    const inputErrors = getFormInput(id)?.error || [];
    return !!inputErrors.length;
  };

  const formatErrorMessage = (key: DynamicFormInputItems["key"], errorMessage: string) => {
    if (errorMessage.toLowerCase() === "required") {
      const errorKey = `form.validation.${key}.required`;
      return t(errorKey, errorMessage);
    }

    return errorMessage;
  };

  const getInputErrorMessage = (id: IFormInput["id"]) => {
    const inputErrors = getFormInput(id)?.error || [];
    return showInputErrorMessages
      ? inputErrors.map(inputError => formatErrorMessage(id, inputError.message))
      : [];
  };

  const getSubmitBtnDisabled = () => {
    return loading;
  };

  const reset = (resetValues: IResetValues) => {
    const newInputs = resetFormInputs(inputs, resetValues);
    setInputs(newInputs);
    setRequiredFieldsFilled(newInputs);
    setLoading(false);
  };

  const resetInputError = (id: IFormInput["id"]) => {
    const input = get(inputs, id);
    const newInput = { ...input, error: [] as IFormInput["error"] };
    const newInputs = { ...inputs, [id]: newInput };
    setInputs(newInputs);
    return newInputs;
  };

  const processServerValidationErrors = (thunkError: IAppError | null) => {
    const newInputs = processValidationErrors(inputs, thunkError?.validationErrors);
    setInputs(newInputs);
    return newInputs;
  };
  /*
  ..................
  ~~~~~ END HELPERS ~~~~~
  ..................
  */

  /*
  ..................
  ~~~~~ START EVENT HANDLERS ~~~~~
  ..................
  */

  const onBlur = (event: { target: { id: string } }) => {
    const { id } = event.target;
    validateField(id);
    setFormValidity();
    setFormDirtied();
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files, id, value } = event.target;
    const input = get(inputs, id);
    if (!input) {
      return;
    }

    const newInputs = updateInput(id, {
      value,
      dirty: !isEqual(value, input.pristineValue),
      error: [],
      files,
      touched: true,
    });

    setFormErrorMessage("");
    setRequiredFieldsFilled(newInputs);
  };

  const onSubmit = () => {
    const allFieldsValid = validateAllFields();

    if (!allFieldsValid) {
      return;
    }

    onSubmitCb && onSubmitCb(form);
  };

  /*
  ..................
  ~~~~~ END EVENT HANDLERS ~~~~~
  ..................
  */
  const form = {
    onChange,
    onBlur,
    onSubmit,
    getFormInput,
    getInputError,
    getInputErrorMessage,
    validateAllFields,
    getSubmitBtnDisabled,
    setLoading,
    setFormErrorMessage,
    loading,
    formErrorMessage,
    allRequiredFieldsFilled,
    valid: formvalid,
    reset,
    resetInputError,
    processServerValidationErrors,
    setInputs,
    initializeFormState,
    isFormDirtied,
    isFormInitialized,
  };

  useEffect(() => {
    form.initializeFormState(inputs);
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  return { form, inputs };
};
